import { Fragment, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { tss } from "tss-react";
import { defaultViewProgram } from "constants/viewer";
import { getWorkflowsVersions, findSelectedObjects } from "actions/GlobalActions";
import { FoldersActions } from "actions";
import ViewerEvents from "events/ViewerEvents";
import * as Utils from "services/UtilsService";
import ObjectInfo from "./ObjectInfo";
import ObjectVersion from "./ObjectVersion";
import commonStyles, { iconColor } from "styles/common";

const useStyles = tss.create({
    rowSmall: {
        width: 150,
        overflow: "hidden",
    },
    selectedBox: {
        width: 160,
        backgroundColor: "rgb(245,245,245)",
        position: "relative",
        top: 8,
        borderRadius: 4,
    },
    rowContent: {
        width: "100%",
        display: "inline-flex",
        height: 40,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    },
    chipContainer: {
        width: "100%",
        position: "relative",
        display: "inline-flex",
        top: 9,
        left: 19,
    },
    icon: {
        position: "relative",
        width: 16,
        height: 16,
        verticalAlign: "middle",
        display: "inline-block",
        color: iconColor,
    },
    iconLayer: {
        left: 5,
        top: 8,
        width: 24,
        height: 24,
    },
    selected: {
        backgroundColor: "rgb(235,238,244)",
    },
    label: {
        fontSize: 14,
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: 200,
    },
    checkboxContainer: {
        position: "relative",
    },
    right: {
        float: "right",
    },
    top: {
        verticalAlign: "top",
    },
    labelContainer: {
        width: "100%",
        position: "relative",
        display: "inline-flex",
        top: 9,
        left: 19,
    },
    list: {
        paddingLeft: 36,
    },
});

function isViewable(item: any) {
    if (item.state !== "COMPLETE") return false;
    if (item.file_type === "VOXOP") return false;
    if (typeof defaultViewProgram[item.file_type] === "undefined") return false;

    return true;
}

export const getVersions = (object: any, versions: any, setEntity: any) => {
    if (!versions) {
        return <div />;
    }
    versions = Utils.sortArrayByProp(versions, "file_date", true);
    return (
        <Fragment>
            {versions.map((child: any, index: number) => (
                <ObjectVersion
                    key={"program-" + index}
                    index={index}
                    object={child}
                    setEntity={setEntity}
                    selected={object["ID"] === child["ID"]}
                />
            ))}
        </Fragment>
    );
};

type IViewableProps = {
    selected: any;
    setSelected: any;
    filter: string;
    selectedBox: any;
};

let allObjects: any;

export default function AddObjectsList(props: IViewableProps) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { filter, selected, setSelected, selectedBox } = props;
    const { view, currentProject } = ViewerEvents;
    const [filteredObjects, setFilteredObjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState({});

    useEffect(() => {
        if (!currentProject) return;
        
        const fetchData = (callback: any) => {
            let entities = Object.values(currentProject);
            entities = entities.filter(
                (entity: any) =>
                    isViewable(entity) &&
                    entity.file_type !== "VIEW" &&
                    entity.file_type !== "REPORT" &&
                    entity.name.toLowerCase().includes(filter)
            );
            allObjects = getWorkflowsVersions(entities);
            callback(allObjects);
        };
        console.time("fetchObjectsData");
        setLoading(true);
        fetchData((allObjects: any) => {
            const renderedObjects = findSelectedObjects(allObjects, selectedEntities);
            setFilteredObjects(renderedObjects);
            setLoading(false);
            console.timeEnd("fetchObjectsData");
        });
    }, [currentProject, filter, selectedEntities]);

    if (!view?.name) {
        return <div />;
    }

    if (loading) {
        return (
            <div className={common.middleCenter} style={{ width: "100%", height: "100%" }}>
                <CircularProgress />
            </div>
        );
    }
    const setEntity = (index: any, entry: any) => {
        const se: any = { ...selectedEntities };
        const workflow_id = FoldersActions.getEntityWorkflow(entry) || "unversioned";
        se[workflow_id] = entry;
        setSelectedEntities(se);
    };

    const updateSelection = (evt: any) => {
        if (evt.target) {
            const index = evt.target.tabIndex;
            //console.log("index:", index);
            const elem = document.getElementById("layer-label-" + index);
            if (elem && elem.firstChild) {
                if (evt.target.checked) {
                    const div = document.createElement("div");
                    div.className = classes.rowSmall;
                    const cloneNode = elem.firstChild.cloneNode(true);
                    const checkbox = cloneNode.lastChild;
                    if (checkbox) {
                        cloneNode.removeChild(checkbox);
                    }
                    div.appendChild(cloneNode);
                    selectedBox.appendChild(div);
                    const newSelection: any = [...selected];
                    newSelection.push(evt.target);
                    setSelected(newSelection);
                } else {
                    const newSelection: any = [...selected];
                    let deletedIndex = selected.findIndex((elm: any) => elm.tabIndex === elem.tabIndex);
                    newSelection.splice(deletedIndex, 1);
                    setSelected(newSelection);
                    const removedNode = selectedBox.childNodes[deletedIndex + 1];
                    selectedBox.removeChild(removedNode);
                }
            }
        }
    };
    return (
        <div className={classes.top} style={{ width: "calc(100% - 10px)" }}>
            {filteredObjects.map((object: any, index: number) => {
                const workflow_id = FoldersActions.getEntityWorkflow(object) || "unversioned";
                const versions = allObjects[workflow_id][object["name"]];
                return (
                    <div key={"object-" + index} id={"layer-label-" + index} tabIndex={index}>
                        <ObjectInfo
                            index={index}
                            object={object}
                            setEntity={setEntity}
                            hasChildren={true}
                            hasCheckbox={true}
                            updateSelection={updateSelection}
                        >
                            <div className={classes.list}>{getVersions(object, versions, setEntity)}</div>
                        </ObjectInfo>
                    </div>
                );
            })}
        </div>
    );
}
